import React, { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  H2,
  Link,
  Preloader,
  Toggle,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import {
  ActiveAndPeriod,
  AddClientModal,
  AddPositionModal,
  AreasAction,
  Clients,
  IFormValues,
  MainInfo,
  Positions,
} from 'components';
import { LocalizationContext } from 'localization';
import {
  CREATE_PROMO_CODE,
  DELETE_PROMO_CODE,
  GET_PROMO_CODE,
  ICreatePromoCodeRequest,
  ICreatePromoCodeResponse,
  IDeletePromoCodeRequest,
  IDeletePromoCodeResponse,
  IGetPromoCodeRequest,
  IGetPromoCodeResponse,
  IUpdatePromoCodeRequest,
  IUpdatePromoCodeResponse,
  TPromoCodeData,
  UPDATE_PROMO_CODE,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { EPromoScopeType, EPromoValueType } from 'types';

import { DEFAULT_VALUES } from './constants';
import {
  ETabsModal,
  IApplicableParamsParsed,
  IChosenClientItem,
  IChosenPositions,
} from './interfaces';
import s from './PromoCodeManager.module.scss';
import { normaliseSubmitPromoInput } from './utils';

export const PromoCodeManager: FC = () => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    state: {
      settings: { telegramChannelId },
    },
  } = useContext(SettingsContext);

  const [openPositionModal, setOpenPositionModal] = useState<boolean>(false);
  const [openClientModal, setOpenClientModal] = useState<boolean>(false);

  const [chosenPositions, setChosenPositions] = useState<
    Array<IChosenPositions>
  >([]);
  const [chosenItems, setChosenItems] = useState<IChosenClientItem[]>([]);
  const areasPromo = [
    {
      title: t('settings.tabs.billing'),
      value: EPromoScopeType.BILLING,
      active: false,
      disabled: false,
    },
    {
      title: t('promo.areas.mobile.app'),
      value: EPromoScopeType.MOBILE_APP,
      active: false,
      disabled: false,
    },
    {
      title: 'Shell',
      value: EPromoScopeType.SHELL,
      active: false,
      disabled: false,
    },
  ];
  const [areas, setAreas] = useState(areasPromo);
  const [initialAreas, setInitialAreas] = useState(areasPromo);
  const [initialChosenPositions, setInitialChosenPositions] = useState<
    Array<IChosenPositions>
  >([]);
  const [initialChosenItems, setInitialChosenItems] = useState<
    IChosenClientItem[]
  >([]);
  const [isOverallDirty, setIsOverallDirty] = useState(false);
  const [initialActiveFrom, setInitialActiveFrom] = useState<string | null>(
    null
  );
  const [initialActiveTo, setInitialActiveTo] = useState<string | null>(null);
  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    getValues,
    trigger,
    clearErrors,
    formState: { isDirty },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const resetFormData = (promoCodeData: TPromoCodeData): void => {
    reset({
      code: promoCodeData?.code,
      active: promoCodeData?.active,
      amount:
        promoCodeData?.client_scope?.[0]?.__typename === 'ScopedClient'
          ? 1
          : promoCodeData?.amount,
      rewardType: promoCodeData?.value.type,
      rewardValue: promoCodeData?.value.value,
      is_tracked: promoCodeData?.is_tracked,
      activeTo: promoCodeData?.active_to,
      activeFrom: promoCodeData?.active_from,
    });
    setInitialActiveFrom(promoCodeData.active_from);
    setInitialActiveTo(promoCodeData.active_to);

    if (promoCodeData.scopes) {
      const newAreas = areas.map((area) => ({
        ...area,
        active: promoCodeData.scopes.includes(area.value),
      }));
      setAreas(newAreas);
      setInitialAreas(newAreas);
    }

    if (promoCodeData.client_scope) {
      const normalizeClientScope = promoCodeData.client_scope.map(
        (item): IChosenClientItem => ({
          id: item.id,
          type: item.__typename === 'ScopedClient' ? 'client' : 'group',
          title:
            item.__typename === 'ScopedClient'
              ? (item.phone as string)
              : (item.title as string),
        })
      );

      setChosenItems(normalizeClientScope);
      setInitialChosenItems([...normalizeClientScope]);
    }

    if (promoCodeData.applicable_params) {
      const data: IApplicableParamsParsed = JSON.parse(
        promoCodeData.applicable_params
      );

      const normalizeApplicableParams: Array<IChosenPositions> = [];

      for (const type in data) {
        const item = data[type as 'goods' | 'services' | 'tariffs'];
        let normalizeApplicableParamsType: ETabsModal | undefined;
        let normalizeApplicableParamsTitle = '';

        switch (type) {
          case 'goods':
            normalizeApplicableParamsType = ETabsModal.GOODS;
            normalizeApplicableParamsTitle = t('promo.position.all_goods');
            break;
          case 'services':
            normalizeApplicableParamsType = ETabsModal.SERVICES;
            normalizeApplicableParamsTitle = t('promo.position.all_services');
            break;
          case 'tariffs':
            normalizeApplicableParamsType = ETabsModal.TARIFFS;
            normalizeApplicableParamsTitle = t('promo.position.all_tariffs');
            break;
          default:
            normalizeApplicableParamsTitle = t('visitors.types.all');
            break;
        }

        if (item?.all) {
          normalizeApplicableParams.push({
            id: -1,
            type: normalizeApplicableParamsType,
            title: normalizeApplicableParamsTitle,
          });
        } else if (item?.items) {
          item.items.forEach(({ title, id }) =>
            normalizeApplicableParams.push({
              type: normalizeApplicableParamsType,
              id,
              title,
            })
          );
        }
      }

      setChosenPositions(normalizeApplicableParams);
      setInitialChosenPositions([...normalizeApplicableParams]);
    }
  };

  const [createCode, { loading: createLoading }] = useMutation<
    ICreatePromoCodeResponse,
    ICreatePromoCodeRequest
  >(CREATE_PROMO_CODE, {
    onCompleted: (res) => {
      if (!res.createPromoCode) return;
      notification.info({
        t,
        type: 'success',
        title: t('promo.created'),
        dismiss: 5,
      });

      navigate(`/promo-codes`, { replace: true });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        message: error?.message,
        title: t('errors.error'),
        dismiss: 5,
      });
    },
  });

  const [updateCode, { loading: updateLoading }] = useMutation<
    IUpdatePromoCodeResponse,
    IUpdatePromoCodeRequest
  >(UPDATE_PROMO_CODE, {
    refetchQueries: [],
    awaitRefetchQueries: false,
    onCompleted: (res) => {
      if (!res.updatePromoCode) return;

      resetFormData(res.updatePromoCode);
      navigate(`/promo-codes`, { replace: true });
      notification.info({
        t,
        type: 'success',
        title: t('promo.updated'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        message: error?.message,
        title: t('errors.error'),
        dismiss: 5,
      });
    },
  });

  const [deleteCode, { loading: deleteLoading }] = useMutation<
    IDeletePromoCodeResponse,
    IDeletePromoCodeRequest
  >(DELETE_PROMO_CODE, {
    onCompleted: (res) => {
      if (!res.deletePromoCode) return;

      navigate(`/promo-codes`, { replace: true });
      notification.info({
        t,
        type: 'success',
        title: t('promo.deleted'),
        dismiss: 5,
      });
    },
    onError: (error) => {
      notification.info({
        t,
        type: 'error',
        message: error?.message,
        title: t('errors.error'),
        dismiss: 5,
      });
    },
  });

  const [getPromoCode, { data: promoCodeFetchData }] = useLazyQuery<
    IGetPromoCodeResponse,
    IGetPromoCodeRequest
  >(GET_PROMO_CODE, {
    onCompleted: (res) => {
      if (!res.promoCode) return;

      resetFormData(res.promoCode);
    },
  });

  const onSubmit = (values: IFormValues): void => {
    const input = normaliseSubmitPromoInput({
      values,
      chosenItems,
      chosenPositions,
      areas,
      rewardType,
    });
    if (id) {
      updateCode({
        variables: {
          id: Number(id),
          input,
        },
      });
    } else {
      createCode({
        variables: {
          input,
        },
      });
    }
  };

  useEffect(() => {
    if (!id) return;

    getPromoCode({ variables: { promoCodeId: Number(id) } });
  }, [id]);

  const watchedActiveFrom = watch('activeFrom');
  const watchedActiveTo = watch('activeTo');
  const rewardType = watch('rewardType');

  useEffect(() => {
    setTimeout(() => {
      if (rewardType === EPromoValueType.DISCOUNT) {
        setAreas((prev) =>
          prev.map((area) => ({
            ...area,
            active: area.value === EPromoScopeType.BILLING,
            disabled: area.value !== EPromoScopeType.BILLING,
          }))
        );
      } else {
        setAreas((prev) =>
          prev.map((area) => ({
            ...area,
            disabled: false,
          }))
        );
      }
    }, 0);
  }, [rewardType]);

  useEffect(() => {
    const hasAreasChanged =
      JSON.stringify(areas) !== JSON.stringify(initialAreas);
    const hasPositionsChanged =
      JSON.stringify(chosenPositions) !==
      JSON.stringify(initialChosenPositions);
    const hasItemsChanged =
      JSON.stringify(chosenItems) !== JSON.stringify(initialChosenItems);
    const hasActiveFromChanged = watchedActiveFrom !== initialActiveFrom;
    const hasActiveToChanged = watchedActiveTo !== initialActiveTo;

    const hasFormChanges =
      isDirty ||
      hasAreasChanged ||
      hasPositionsChanged ||
      hasItemsChanged ||
      hasActiveFromChanged ||
      hasActiveToChanged;

    const hasActiveAreas = areas.some((item) => item.active);

    setIsOverallDirty(hasFormChanges && hasActiveAreas);
  }, [
    isDirty,
    areas,
    chosenPositions,
    chosenItems,
    initialAreas,
    initialChosenPositions,
    initialChosenItems,
    initialActiveFrom,
    initialActiveTo,
    watchedActiveFrom,
    watchedActiveTo,
  ]);

  return (
    <Preloader loading={false}>
      {openClientModal && (
        <AddClientModal
          onClose={() => setOpenClientModal(false)}
          chosenItems={chosenItems}
          setChosenItems={setChosenItems}
        />
      )}

      {openPositionModal && (
        <AddPositionModal
          onClose={() => setOpenPositionModal(false)}
          chosenPositions={chosenPositions}
          setChosenPositions={setChosenPositions}
        />
      )}

      <div className={s.container}>
        <div className={s.link_back} onClick={() => navigate('/promo-codes')}>
          <BackwardIcon />
          {t('back')}
        </div>
        <div className={s.content}>
          <div className={s.header_block}>
            <H2>{!id ? t('add.promo_code') : t('promo.editing')}</H2>

            {!id && (
              <Tooltip
                content={
                  <div className={s.tooltip_content}>
                    <span className={s.text}>
                      {t('promo.manager.tooltip.title')}
                    </span>
                    <Link
                      title={t('more')}
                      link={
                        'https://support.smartshell.gg/hc/ru/articles/16510803851021-%D0%9F%D1%80%D0%BE%D0%BC%D0%BE%D0%BA%D0%BE%D0%B4'
                      }
                    />
                  </div>
                }
                contentClassName={s.tooltip}
              />
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className={s.form}>
            <MainInfo
              control={control}
              setValue={setValue}
              watch={watch}
              totalCodes={promoCodeFetchData?.promoCode?.amount}
              usedCodes={promoCodeFetchData?.promoCode?.counters?.used}
              clearErrors={clearErrors}
            />

            <Clients
              control={control}
              setValue={setValue}
              watch={watch}
              setOpenClientModal={setOpenClientModal}
              chosenItems={chosenItems}
              setChosenItems={setChosenItems}
              promoOrPromoCampaign={'promo'}
              trigger={trigger}
              clearErrors={clearErrors}
            />

            <Positions
              control={control}
              setValue={setValue}
              watch={watch}
              setOpenPositionModal={setOpenPositionModal}
              chosenPositions={chosenPositions}
              setChosenPositions={setChosenPositions}
              rewardType={rewardType}
            />

            <AreasAction
              areas={areas}
              setAreas={setAreas}
              rewardType={rewardType}
            />

            <ActiveAndPeriod
              control={control}
              setValue={setValue}
              activeTo={promoCodeFetchData?.promoCode.active_to || null}
              activeFrom={promoCodeFetchData?.promoCode.active_from || null}
            />

            <div className={cn(s.frame, !telegramChannelId?.id && s.disable)}>
              <Controller
                render={({ field }) => (
                  <Toggle
                    {...field}
                    iconName="TelegramBellIcon"
                    label={t('promo.telegram.text')}
                    disabled={!telegramChannelId?.id}
                    className={s.toggle}
                  />
                )}
                name="is_tracked"
                control={control}
              />
              {!telegramChannelId?.id && (
                <span className={s.text_link}>
                  {t('telegramModal.info.text_3')}{' '}
                  <NavLink
                    state={{ selectedTab: 'INTEGRATIONS' }}
                    to={'/settings'}
                    className={s.link}
                  >
                    {t('integration')}
                  </NavLink>{' '}
                </span>
              )}
            </div>
            <div className={s.buttons_block}>
              {id && (
                <Button
                  className={s.button}
                  color="red"
                  variant="secondary"
                  isLoading={deleteLoading}
                  onClick={() => {
                    openConfirmDialog(t, 'remove', {
                      title: t('log.promo.deleted'),
                      message: t('promo.delete.text', getValues('code')),
                      acceptTitle: t('delete'),
                      declineTitle: t('cancel'),
                    }).then(({ status }) => {
                      if (status === 'ACCEPTED') {
                        deleteCode({ variables: { id: Number(id) } });
                      }
                    });
                  }}
                >
                  {t('delete')}
                </Button>
              )}
              {chosenPositions.length === 0 &&
              rewardType === EPromoValueType.DISCOUNT ? (
                <Tooltip
                  className={cn(s.button, s.button_save)}
                  content={t('promo.button.submit.disabled')}
                  position="top"
                  contentClassName={s.tooltip}
                >
                  <Button
                    className={cn(s.button, s.button_save)}
                    type="submit"
                    isLoading={createLoading || updateLoading}
                    disabled={!isOverallDirty || chosenPositions.length === 0}
                  >
                    {id ? t('save') : t('add')}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  className={cn(s.button, s.button_save)}
                  type="submit"
                  isLoading={createLoading || updateLoading}
                  disabled={!isOverallDirty}
                >
                  {id ? t('save') : t('add')}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Preloader>
  );
};
